import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import Sidebar from "../components/Sidebar";
import PageTopBar from "../components/PageTopBar";
import {
    ScrollText,
    ChevronLeft,
    ChevronRight,
    Filter,
    Download,
    Copy,
} from "lucide-react";
import { toast } from "sonner";
import { useCurrencyPref } from "./admin/_shared";

const PAGE_SIZE = 50;
// Hide the "(vía fal)" marketing suffix from model names in user-facing logs.
const cleanModelName = (name) =>
    (name || "").replace(/\s*\(?\s*v[ií]a\s+fal\s*\)?/gi, "").trim();
const fmtCop = (v) =>
    `$${new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(v || 0)}`;
const fmtUsd = (v) =>
    `$${(v || 0).toLocaleString("es-CO", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

/**
 * Render a COP-canonical value in the user's chosen currency. When trm is
 * positive and currency is USD, divide; otherwise show COP. We keep both as
 * a `title` tooltip so they can still see the alternate currency on hover.
 */
function fmtMoney(cop, currency, trm) {
    if (currency === "USD" && trm > 0) {
        return fmtUsd((cop || 0) / trm);
    }
    return fmtCop(cop);
}

export default function Logs() {
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [pageTotalCop, setPageTotalCop] = useState(0);
    const [users, setUsers] = useState([]);
    const [models, setModels] = useState([]);
    const [filters, setFilters] = useState({ user_id: "", model_id: "", brand_id: "" });
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    // Currency + TRM follow the platform-wide preference; reads from /credits
    // so historical rows convert with the global TRM (close enough for an
    // auditing view).
    const [backendCurrency, setBackendCurrency] = useState("USD");
    const [currency] = useCurrencyPref(backendCurrency);
    const [trm, setTrm] = useState(4000);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    useEffect(() => {
        api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
        api.get("/admin/models").then((r) => setModels(r.data || [])).catch(() => {});
        api.get("/brands").then((r) => setBrands(r.data || [])).catch(() => {});
        api.get("/credits")
            .then((r) => {
                setBackendCurrency(r.data.currency || "USD");
                setTrm(Number(r.data.trm_cop_per_usd || 4000));
            })
            .catch(() => {});
    }, []);

    const load = () => {
        setLoading(true);
        const params = new URLSearchParams({
            skip: String((page - 1) * PAGE_SIZE),
            limit: String(PAGE_SIZE),
        });
        if (filters.user_id) params.set("user_id", filters.user_id);
        if (filters.model_id) params.set("model_id", filters.model_id);
        if (filters.brand_id) params.set("brand_id", filters.brand_id);
        api.get(`/admin/logs?${params.toString()}`)
            .then((r) => {
                setRows(r.data.items || []);
                setTotal(r.data.total || 0);
                setPageTotalCop(r.data.page_cop_total || 0);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filters]);

    // Reset to page 1 on filter change
    useEffect(() => {
        setPage(1);
    }, [filters]);

    const pageTotalImages = rows.length;

    const exportCsv = () => {
        if (rows.length === 0) return;
        const header = [
            "fecha",
            "usuario",
            "email",
            "rol",
            "marca",
            "modelo",
            "resolución",
            "calidad",
            "imágenes_extra",
            "tiempo_generacion_s",
            "costo_modelo_cop",
            "sobrecargo_calidad_cop",
            "costo_total_cop",
            "costo_total_usd",
            "trm",
            "prompt",
        ];
        const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
        const body = rows
            .map((r) =>
                [
                    r.created_at,
                    r.user_name,
                    r.user_email,
                    r.user_role,
                    r.brand_name,
                    cleanModelName(r.model_name),
                    `${r.width}x${r.height}`,
                    r.quality || "basic",
                    r.extras_count ?? 0,
                    r.generation_seconds ?? "",
                    r.base_model_cop ?? "",
                    r.quality_surcharge_cop ?? 0,
                    r.cop_cost,
                    trm > 0 ? ((r.cop_cost || 0) / trm).toFixed(4) : "",
                    trm,
                    r.prompt,
                ]
                    .map(escape)
                    .join(","),
            )
            .join("\n");
        const csv = header.join(",") + "\n" + body;
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `krea-logs-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("CSV descargado");
    };

    const activeFilters = useMemo(
        () => Object.values(filters).filter(Boolean).length,
        [filters],
    );

    return (
        <div className="flex min-h-screen bg-ink-950">
            <Sidebar />
            <main className="flex-1 overflow-y-auto">
                <PageTopBar />
                <div className="max-w-7xl mx-auto px-10 py-14 space-y-8">
                    <header className="flex items-end justify-between gap-6 flex-wrap">
                        <div>
                            <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase mb-2 flex items-center gap-2">
                                <ScrollText className="w-3 h-3" />
                                Auditoría
                            </p>
                            <h1 className="font-display text-5xl text-fog-100">Logs</h1>
                            <p className="text-fog-400 text-sm mt-2 max-w-xl">
                                Cada imagen generada: quién, con qué modelo, cuánto costó y el prompt refinado. Úsalo para revisar consumo en detalle.
                            </p>
                        </div>
                        <button
                            data-testid="logs-export-csv"
                            onClick={exportCsv}
                            disabled={rows.length === 0}
                            className="rounded-xl border hairline border-ink-600 text-fog-300 hover:text-periwinkle-500 hover:border-periwinkle-500 px-4 py-2.5 text-xs font-mono uppercase tracking-widest flex items-center gap-2 disabled:opacity-40"
                        >
                            <Download className="w-3.5 h-3.5" />
                            Exportar CSV
                        </button>
                    </header>

                    {/* Filters */}
                    <section
                        data-testid="logs-filters"
                        className="rounded-2xl border hairline border-ink-600 bg-ink-900 p-4 flex items-center gap-3 flex-wrap"
                    >
                        <div className="flex items-center gap-2 text-fog-400">
                            <Filter className="w-3.5 h-3.5" />
                            <span className="font-mono text-[10px] tracking-widest uppercase">
                                Filtrar · {activeFilters} activos
                            </span>
                        </div>
                        <select
                            data-testid="logs-filter-user"
                            value={filters.user_id}
                            onChange={(e) =>
                                setFilters((f) => ({ ...f, user_id: e.target.value }))
                            }
                            className="rounded-lg bg-ink-800 border hairline border-ink-600 text-fog-100 text-xs px-3 py-2 outline-none focus:border-periwinkle-500"
                        >
                            <option value="">Todos los usuarios</option>
                            {users.map((u) => (
                                <option key={u.id} value={u.id}>
                                    {u.name || u.email}
                                </option>
                            ))}
                        </select>
                        <select
                            data-testid="logs-filter-model"
                            value={filters.model_id}
                            onChange={(e) =>
                                setFilters((f) => ({ ...f, model_id: e.target.value }))
                            }
                            className="rounded-lg bg-ink-800 border hairline border-ink-600 text-fog-100 text-xs px-3 py-2 outline-none focus:border-periwinkle-500"
                        >
                            <option value="">Todos los modelos</option>
                            {models.map((m) => (
                                <option key={m.id} value={m.id}>
                                    {m.name}
                                </option>
                            ))}
                        </select>
                        <select
                            data-testid="logs-filter-brand"
                            value={filters.brand_id}
                            onChange={(e) =>
                                setFilters((f) => ({ ...f, brand_id: e.target.value }))
                            }
                            className="rounded-lg bg-ink-800 border hairline border-ink-600 text-fog-100 text-xs px-3 py-2 outline-none focus:border-periwinkle-500"
                        >
                            <option value="">Todas las marcas</option>
                            {brands.map((b) => (
                                <option key={b.id} value={b.id}>
                                    {b.name}
                                </option>
                            ))}
                        </select>
                        {activeFilters > 0 && (
                            <button
                                data-testid="logs-filter-clear"
                                onClick={() =>
                                    setFilters({ user_id: "", model_id: "", brand_id: "" })
                                }
                                className="rounded-lg border hairline border-ink-600 text-fog-400 hover:text-fog-100 px-3 py-2 text-xs font-mono uppercase tracking-widest"
                            >
                                Limpiar
                            </button>
                        )}
                    </section>

                    {/* Summary line */}
                    <div
                        data-testid="logs-summary"
                        className="flex items-center gap-6 flex-wrap px-4 py-3 rounded-xl bg-ink-900/60 border hairline border-ink-600"
                    >
                        <div>
                            <p className="font-mono text-[9px] tracking-widest text-fog-400 uppercase">
                                Resultados
                            </p>
                            <p className="font-display text-xl text-fog-100">
                                {total.toLocaleString("es-CO")}
                            </p>
                        </div>
                        <div>
                            <p className="font-mono text-[9px] tracking-widest text-fog-400 uppercase">
                                Página
                            </p>
                            <p className="font-display text-xl text-fog-100">
                                {pageTotalImages.toLocaleString("es-CO")} imgs
                            </p>
                        </div>
                        <div title={currency === "USD" ? `≈ ${fmtCop(pageTotalCop)} COP` : `≈ ${fmtUsd(pageTotalCop / (trm || 1))} USD`}>
                            <p className="font-mono text-[9px] tracking-widest text-fog-400 uppercase">
                                Costo de la página
                            </p>
                            <p className="font-display text-xl text-periwinkle-500" data-testid="logs-page-total">
                                {fmtMoney(pageTotalCop, currency, trm)}
                            </p>
                        </div>
                    </div>

                    {/* Table */}
                    <div
                        data-testid="logs-table"
                        className="rounded-2xl border hairline border-ink-600 bg-ink-900 overflow-x-auto"
                    >
                        <table className="min-w-full text-xs">
                            <thead>
                                <tr className="border-b hairline border-ink-600">
                                    <Th>Fecha</Th>
                                    <Th>Usuario</Th>
                                    <Th>Marca</Th>
                                    <Th>Modelo</Th>
                                    <Th>Resolución</Th>
                                    <Th>Tiempo</Th>
                                    <Th className="text-right">Costo</Th>
                                    <Th>Prompt</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr>
                                        <td colSpan={8} className="px-5 py-10 text-center text-xs text-fog-400 font-mono">
                                            Cargando…
                                        </td>
                                    </tr>
                                )}
                                {!loading && rows.length === 0 && (
                                    <tr>
                                        <td colSpan={8} className="px-5 py-10 text-center text-xs text-fog-500 font-mono">
                                            Sin registros.
                                        </td>
                                    </tr>
                                )}
                                {rows.map((r) => (
                                    <LogRow key={r.id} row={r} currency={currency} trm={trm} />
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {total > 0 && (
                        <Pagination
                            page={page}
                            totalPages={totalPages}
                            total={total}
                            onChange={setPage}
                        />
                    )}
                </div>
            </main>
        </div>
    );
}

function Th({ children, className = "" }) {
    return (
        <th
            className={`text-left px-4 py-3 font-mono text-[10px] tracking-widest text-fog-400 uppercase ${className}`}
        >
            {children}
        </th>
    );
}

function LogRow({ row, currency, trm }) {
    const d = new Date(row.created_at);
    const date = d.toLocaleDateString("es-CO", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
    });
    const time = d.toLocaleTimeString("es-CO", {
        hour: "2-digit",
        minute: "2-digit",
    });
    // Show the alternate currency on hover so the auditor can verify either.
    const baseCop = Number(row.base_model_cop || 0);
    const surchargeCop = Number(row.quality_surcharge_cop || 0);
    const altLabel =
        currency === "USD"
            ? `≈ ${fmtCop(row.cop_cost)} COP`
            : `≈ ${fmtUsd((row.cop_cost || 0) / (trm || 1))} USD`;
    const qualityLabel =
        { basic: "Básico", medium: "Moderado", high: "Súper" }[row.quality] ||
        row.quality ||
        "—";
    const costBreakdown =
        surchargeCop > 0
            ? `Modelo ${fmtMoney(baseCop, currency, trm)} + calidad ${qualityLabel} ${fmtMoney(surchargeCop, currency, trm)}`
            : "";
    return (
        <tr
            data-testid={`logs-row-${row.id}`}
            className="border-b hairline border-ink-600 last:border-0 hover:bg-ink-800/40 transition-colors"
        >
            <td className="px-4 py-3 whitespace-nowrap">
                <p className="text-fog-100">{date}</p>
                <p className="font-mono text-[10px] text-fog-500">{time}</p>
            </td>
            <td className="px-4 py-3">
                <p className="text-fog-100 truncate max-w-[180px]" title={row.user_name}>
                    {row.user_name}
                </p>
                <p className="font-mono text-[10px] text-fog-500 truncate max-w-[180px]">
                    {row.user_email}
                </p>
            </td>
            <td className="px-4 py-3">
                <span className="inline-flex items-center gap-1.5">
                    <span
                        className="w-1.5 h-1.5 rounded-full shrink-0"
                        style={{ background: row.brand_color }}
                    />
                    <span className="text-fog-100 truncate max-w-[120px]">
                        {row.brand_name}
                    </span>
                </span>
            </td>
            <td className="px-4 py-3">
                <p className="text-fog-100 truncate max-w-[180px]">{cleanModelName(row.model_name)}</p>
                <p className="font-mono text-[9px] text-fog-500 tracking-wider uppercase">
                    {row.model_family}
                </p>
            </td>
            <td className="px-4 py-3 font-mono text-[11px] text-fog-300 whitespace-nowrap">
                {/* For text generations (kind=copy_text) we hijack the
                  * resolution column to surface input/output tokens —
                  * these rows have no width/height in pixels, but the
                  * library doc stored tokens_in/tokens_out under those
                  * keys for log compatibility. */}
                {row.kind === "copy_text" ? (
                    <>
                        <p className="text-emerald-400">{row.width}↑ / {row.height}↓</p>
                        <p className="font-mono text-[9px] text-fog-500 tracking-wider uppercase">
                            tokens
                        </p>
                    </>
                ) : (
                    <>
                        <p>{row.width}×{row.height}</p>
                        {row.quality && row.quality !== "basic" && (
                            <p className="font-mono text-[9px] text-periwinkle-400 tracking-wider uppercase">
                                {qualityLabel}
                            </p>
                        )}
                        {row.extras_count > 0 && (
                            <p className="font-mono text-[9px] text-amber-400 tracking-wider uppercase">
                                +{row.extras_count} ref. extra
                            </p>
                        )}
                    </>
                )}
            </td>
            <td className="px-4 py-3 font-mono text-[11px] text-fog-300 whitespace-nowrap">
                {(() => {
                    const secs = Number(row.generation_seconds);
                    if (!Number.isFinite(secs) || secs <= 0) {
                        return <span className="text-fog-500">—</span>;
                    }
                    let label;
                    if (secs >= 60) {
                        const m = Math.floor(secs / 60);
                        const s = Math.round(secs - m * 60);
                        label = s > 0 ? `${m} min ${s} s` : `${m} min`;
                    } else {
                        label = `${secs.toFixed(secs < 10 ? 1 : 0)} s`;
                    }
                    return <span className="text-periwinkle-400">{label}</span>;
                })()}
            </td>
            <td
                className="px-4 py-3 text-right font-display text-periwinkle-500"
                title={costBreakdown ? `${costBreakdown}  ·  ${altLabel}` : altLabel}
            >
                <p>{fmtMoney(row.cop_cost, currency, trm)}</p>
                {surchargeCop > 0 && (
                    <p className="font-mono text-[9px] text-fog-500 mt-0.5">
                        {fmtMoney(baseCop, currency, trm)}
                        <span className="text-periwinkle-400 mx-0.5">+</span>
                        {fmtMoney(surchargeCop, currency, trm)}
                    </p>
                )}
            </td>
            <td className="px-4 py-3 max-w-[320px]">
                <div className="flex items-start gap-2">
                    <p className="text-fog-300 line-clamp-2 flex-1" title={row.prompt}>
                        {row.prompt}
                    </p>
                    <button
                        onClick={() => {
                            navigator.clipboard.writeText(
                                row.refined_prompt || row.prompt,
                            );
                            toast.success("Prompt copiado");
                        }}
                        data-testid={`logs-copy-${row.id}`}
                        className="text-fog-500 hover:text-periwinkle-500 shrink-0"
                        title="Copiar prompt refinado"
                    >
                        <Copy className="w-3 h-3" />
                    </button>
                </div>
            </td>
        </tr>
    );
}

function Pagination({ page, totalPages, total, onChange }) {
    const from = (page - 1) * PAGE_SIZE + 1;
    const to = Math.min(page * PAGE_SIZE, total);
    return (
        <div
            data-testid="logs-pagination"
            className="flex items-center justify-between gap-4 flex-wrap"
        >
            <p className="font-mono text-[10px] tracking-widest text-fog-400 uppercase">
                {from}–{to} de {total}
            </p>
            <div className="flex items-center gap-2">
                <button
                    data-testid="logs-pagination-prev"
                    onClick={() => onChange(Math.max(1, page - 1))}
                    disabled={page <= 1}
                    className="rounded-lg border hairline border-ink-600 text-fog-300 hover:text-periwinkle-500 hover:border-periwinkle-500 w-9 h-9 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label="Anterior"
                >
                    <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="font-mono text-[11px] text-fog-300 px-3">
                    {page} / {totalPages}
                </span>
                <button
                    data-testid="logs-pagination-next"
                    onClick={() => onChange(Math.min(totalPages, page + 1))}
                    disabled={page >= totalPages}
                    className="rounded-lg border hairline border-ink-600 text-fog-300 hover:text-periwinkle-500 hover:border-periwinkle-500 w-9 h-9 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label="Siguiente"
                >
                    <ChevronRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
